import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const EcourtTab = () => {
  // Ecourt state
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewSettingForm, setShowNewSettingForm] = useState(false);
  const [newSetting, setNewSetting] = useState({
    keywords: '',
    startYear: new Date().getFullYear(),
    endYear: new Date().getFullYear() + 1,
    locations: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);

  // Ecourt number settings state
  const [numberSettings, setNumberSettings] = useState([]);
  const [numberSettingsLoading, setNumberSettingsLoading] = useState(false);
  const [showNumberSettingForm, setShowNumberSettingForm] = useState(false);
  const [newNumberSetting, setNewNumberSetting] = useState({
    scraperType: 'ecourtindia',
    cnr_number: ''
  });
  const [numberSettingErrors, setNumberSettingErrors] = useState({});
  const [numberSettingSubmitting, setNumberSettingSubmitting] = useState(false);

  // Location state
  const [locations, setLocations] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationSearchTimeout, setLocationSearchTimeout] = useState(null);

  // Scrape results state
  const [scrapeResults, setScrapeResults] = useState([]);
  const [scrapeLoading, setScrapeLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    skip: 0,
    hasMore: false
  });

  // Ecourt number results state
  const [ecourtNumberResults, setEcourtNumberResults] = useState([]);
  const [ecourtNumberResultsLoading, setEcourtNumberResultsLoading] = useState(false);

  // Fetch locations from API
  useEffect(() => {
    fetchLocations();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (locationSearchTimeout) {
        clearTimeout(locationSearchTimeout);
      }
    };
  }, [locationSearchTimeout]);

  // Load initial scrape results (empty keyword to get all data)
  useEffect(() => {
    searchScrapeResults('', 0);
  }, []);

  // API function to fetch Ecourt number results
  const fetchEcourtNumberResults = async () => {
    try {
      setEcourtNumberResultsLoading(true);
      const response = await api.get('http://45.32.210.228:3000/api/number-data/ecourt-numbers');
      if (response.data.success) {
        // Ensure we have an array of results
        const results = response.data.data;
        if (Array.isArray(results)) {
          setEcourtNumberResults(results);
        } else if (results && Array.isArray(results.results)) {
          setEcourtNumberResults(results.results);
        } else {
          console.warn('Unexpected data structure for ecourt number results:', results);
          setEcourtNumberResults([]);
        }
      } else {
        setEcourtNumberResults([]);
      }
    } catch (error) {
      console.error('Error fetching ecourt number results:', error);
      setEcourtNumberResults([]);
    } finally {
      setEcourtNumberResultsLoading(false);
    }
  };

  // Load Ecourt number results when component mounts
  useEffect(() => {
    fetchEcourtNumberResults();
  }, []);

  const fetchLocations = async () => {
    try {
      setLocationLoading(true);
      const response = await api.get('http://45.32.210.228:3000/api/locations');
      if (response.data.success) {
        setLocations(response.data.locations);
        setFilteredLocations(response.data.locations);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLocationLoading(false);
    }
  };

  // API functions for settings CRUD operations
  const addSetting = async (settingData) => {
    try {
      const response = await api.post('http://45.32.210.228:3000/api/settings/add', settingData);
      return response.data;
    } catch (error) {
      console.error('Error adding setting:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to add setting' };
    }
  };

  const updateSetting = async (settingId, settingData) => {
    try {
      const response = await api.put(`http://45.32.210.228:3000/api/settings/${settingId}`, settingData);
      return response.data;
    } catch (error) {
      console.error('Error updating setting:', error);
      return { success: false, message: error.response?.data?.message || 'Failed to update setting' };
    }
  };

  const deleteSetting = async (settingId) => {
    try {
      const response = await api.delete(`http://45.32.210.228:3000/api/settings/${settingId}`);
      if (response.data.success) {
        console.log('Setting deleted successfully');
        return { success: true };
      } else {
        return { success: false, message: response.data.message || 'Failed to delete setting' };
      }
    } catch (error) {
      console.error('Error deleting setting:', error);
      return { success: false, message: error.response?.data?.message || 'Error deleting setting' };
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('http://45.32.210.228:3000/api/settings/load');
      if (response.data.success) {
        setSettings(response.data.settings.settings || []);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNumberSettings = async () => {
    try {
      setNumberSettingsLoading(true);
      const response = await api.get('http://45.32.210.228:3000/api/number-settings?scraperType=ecourtindia');
      if (response.data.success) {
        setNumberSettings(response.data.data.settings || []);
      }
    } catch (error) {
      console.error('Error fetching number settings:', error);
    } finally {
      setNumberSettingsLoading(false);
    }
  };

  const addNumberSetting = async (settingData) => {
    try {
      setNumberSettingSubmitting(true);
      const response = await api.post('http://45.32.210.228:3000/api/number-settings/add', settingData);
      if (response.data.success) {
        console.log('Number setting added successfully');
        return { success: true };
      } else {
        return { success: false, message: response.data.message || 'Failed to add number setting' };
      }
    } catch (error) {
      console.error('Error adding number setting:', error);
      return { success: false, message: error.response?.data?.message || 'Error adding number setting' };
    } finally {
      setNumberSettingSubmitting(false);
    }
  };

  const deleteNumberSetting = async (settingId) => {
    try {
      const response = await api.delete(`http://45.32.210.228:3000/api/number-settings/ecourtindia/id/${settingId}`);
      if (response.data.success) {
        console.log('Number setting deleted successfully');
        return { success: true };
      } else {
        return { success: false, message: response.data.message || 'Failed to delete number setting' };
      }
    } catch (error) {
      console.error('Error deleting number setting:', error);
      return { success: false, message: error.response?.data?.message || 'Error deleting number setting' };
    }
  };

  const searchScrapeResults = async (keyword, skip = 0) => {
    try {
      setScrapeLoading(true);
      const response = await api.get(`http://45.32.210.228:3000/api/results/search?scraperType=ecourtindia&keywords=${keyword}&skip=${skip}&limit=50`);
      if (response.data.success) {
        if (skip === 0) {
          setScrapeResults(response.data.data.results || []);
        } else {
          setScrapeResults(prev => [...prev, ...(response.data.data.results || [])]);
        }
        setPagination({
          total: response.data.data.total || 0,
          limit: 50,
          skip: skip + 50,
          hasMore: (response.data.data.results || []).length === 50
        });
      }
    } catch (error) {
      console.error('Error searching scrape results:', error);
    } finally {
      setScrapeLoading(false);
    }
  };

  const handleLocationSelect = (location) => {
    const isAlreadySelected = selectedLocations.some(selected => selected.value === location.value);
    if (!isAlreadySelected) {
      setSelectedLocations([...selectedLocations, location]);
    }
    setLocationSearchTerm('');
    setShowLocationDropdown(false);
  };

  const handleLocationRemove = (locationToRemove) => {
    setSelectedLocations(selectedLocations.filter(location => location.value !== locationToRemove.value));
  };

  const handleLocationSearch = (searchTerm) => {
    setLocationSearchTerm(searchTerm);
    
    if (locationSearchTimeout) {
      clearTimeout(locationSearchTimeout);
    }
    
    const timeout = setTimeout(() => {
      if (searchTerm.trim() === '') {
        setFilteredLocations(locations);
      } else {
        const filtered = locations.filter(location =>
          location.text.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredLocations(filtered);
      }
    }, 300);
    
    setLocationSearchTimeout(timeout);
  };

  // Form handling functions
  const validateForm = (data) => {
    const errors = {};
    if (!data.keywords) {
      errors.keywords = 'Keywords is required';
    }
    if (!data.startYear || !data.endYear) {
      errors.yearRange = 'Start and end years are required';
    }
    if (data.startYear > data.endYear) {
      errors.yearRange = 'Start year cannot be greater than end year';
    }
    if (data.locations.length === 0) {
      errors.locations = 'At least one location is required';
    }
    return errors;
  };

  const handleAddSetting = async (e) => {
    e.preventDefault();
    
    const settingData = {
      keywords: newSetting.keywords,
      startYear: parseInt(newSetting.startYear),
      endYear: parseInt(newSetting.endYear),
      locations: selectedLocations.map(location => ({
        state: location.stateCode,
        district: location.districtCode,
        ...(location.courtCode != null && location.courtCode !== undefined && { courtComplex: location.courtCode })
      }))
    };

    const errors = validateForm(settingData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const result = await addSetting(settingData);
    if (result.success) {
      // Reset form
      setNewSetting({
        keywords: '',
        startYear: new Date().getFullYear(),
        endYear: new Date().getFullYear() + 1,
        locations: []
      });
      setSelectedLocations([]);
      setFormErrors({});
      setShowNewSettingForm(false);
      
      // Refresh settings list
      fetchSettings();
      
      // Show success message
      alert('Setting added successfully!');
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleEditSetting = async (e) => {
    e.preventDefault();
    
    const settingData = {
      keywords: editingSetting.keywords,
      startYear: parseInt(editingSetting.startYear),
      endYear: parseInt(editingSetting.endYear),
      locations: selectedLocations.map(location => ({
        state: location.stateCode,
        district: location.districtCode,
        ...(location.courtCode != null && location.courtCode !== undefined && { courtComplex: location.courtCode })
      }))
    };

    const errors = validateForm(settingData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const result = await updateSetting(editingSetting.setting_id, settingData);
    if (result.success) {
      // Reset form
      setEditingSetting(null);
      setSelectedLocations([]);
      setFormErrors({});
      setShowEditForm(false);
      
      // Refresh settings list
      fetchSettings();
      
      // Show success message
      alert('Setting updated successfully!');
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleDeleteSetting = async (settingId) => {
    if (window.confirm('Are you sure you want to delete this setting?')) {
      const result = await deleteSetting(settingId);
      if (result.success) {
        // Refresh settings list
        fetchSettings();
        
        // Show success message
        alert('Setting deleted successfully!');
      } else {
        alert(`Error: ${result.message}`);
      }
    }
  };

  const openEditForm = (setting) => {
    console.log('Opening edit form for setting:', setting);
    
    // Convert API locations format to component format (API returns courtComplex, we use courtCode in state)
    const convertedLocations = setting.locations ? setting.locations.map(loc => {
      const locCourtCode = loc.courtComplex ?? loc.courtCode ?? loc.court_code;
      const matchingLocation = locations.find(l =>
        l.stateCode === (loc.state ?? loc.state_code) &&
        l.districtCode === (loc.district ?? loc.district_code) &&
        (locCourtCode == null || (l.courtCode ?? l.court_code) === locCourtCode)
      );
      if (matchingLocation) {
        return matchingLocation;
      }
      return {
        value: locCourtCode != null ? `${loc.state ?? loc.state_code}/${loc.district ?? loc.district_code}/${locCourtCode}` : `${loc.state ?? loc.state_code}/${loc.district ?? loc.district_code}`,
        text: locCourtCode != null ? `${loc.state ?? loc.state_code}/${loc.district ?? loc.district_code}/${locCourtCode}` : `State: ${loc.state ?? loc.state_code}, District: ${loc.district ?? loc.district_code}`,
        stateCode: loc.state ?? loc.state_code,
        districtCode: loc.district ?? loc.district_code,
        ...(locCourtCode != null && { courtCode: locCourtCode })
      };
    }) : [];
    
    setEditingSetting(setting);
    setSelectedLocations(convertedLocations);
    setShowEditForm(true);
  };

  const handleAddNumberSetting = async (e) => {
    e.preventDefault();
    
    if (!newNumberSetting.cnr_number.trim()) {
      setNumberSettingErrors({ cnr_number: 'CNR Number is required' });
      return;
    }

    const result = await addNumberSetting(newNumberSetting);
    if (result.success) {
      // Reset form
      setNewNumberSetting({
        scraperType: 'ecourtindia',
        cnr_number: ''
      });
      setNumberSettingErrors({});
      setShowNumberSettingForm(false);
      
      // Refresh number settings list
      fetchNumberSettings();
      
      // Show success message
      alert('Number setting added successfully!');
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleDeleteNumberSetting = async (settingId) => {
    if (window.confirm('Are you sure you want to delete this number setting?')) {
      const result = await deleteNumberSetting(settingId);
      if (result.success) {
        // Refresh number settings list
        fetchNumberSettings();
        
        // Show success message
        alert('Number setting deleted successfully!');
      } else {
        alert(`Error: ${result.message}`);
      }
    }
  };

  // Add CNR number from scrape result to number settings
  const handleAddToDetail = async (cnrNumber) => {
    if (!cnrNumber) {
      alert('No CNR number available for this result');
      return;
    }

    // Check if CNR number already exists
    const existingSetting = numberSettings.find(setting => setting.cnr_number === cnrNumber);
    if (existingSetting) {
      alert('This CNR number is already in your number settings');
      return;
    }

    // Add the CNR number to number settings
    const settingData = {
      scraperType: 'ecourtindia',
      cnr_number: cnrNumber
    };

    const result = await addNumberSetting(settingData);
    if (result.success) {
      // Refresh number settings list
      fetchNumberSettings();
      
      // Show success message
      alert(`CNR number ${cnrNumber} added to number settings successfully!`);
    } else {
      alert(`Error: ${result.message}`);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchScrapeResults(searchKeyword, 0);
  };

  const handleLoadMore = () => {
    searchScrapeResults(searchKeyword, pagination.skip);
  };

  // Load data when component mounts
  useEffect(() => {
    fetchSettings();
    fetchNumberSettings();
  }, []);

  // Function to extract petitioner and respondent information
  const extractPartyInfo = (petitionerVsRespondent) => {
    if (!petitionerVsRespondent) return { petitioners: '', respondents: '' };
    
    const parts = petitionerVsRespondent.split(' vs. ');
    if (parts.length === 2) {
      return {
        petitioners: parts[0].trim(),
        respondents: parts[1].trim()
      };
    }
    return { petitioners: petitionerVsRespondent, respondents: '' };
  };

  // Function to extract advocate information from documents
  const extractAdvocateInfo = (documents) => {
    if (!documents || !documents['OTHER DOCUMENT(s)']) return { petitionerAdvocates: '', respondentAdvocates: '' };
    
    const docs = documents['OTHER DOCUMENT(s)'];
    const petitionerAdvocates = [];
    const respondentAdvocates = [];
    
    docs.forEach(doc => {
      if (doc['Document Type'] === 'VAKALATNAMA AND MEMO OF APPEARANCE') {
        const filedBy = doc['Filed By'];
        if (filedBy) {
          // This is a simplified logic - in real implementation, you'd need to determine
          // which party the advocate represents based on the case structure
          petitionerAdvocates.push(filedBy);
        }
      }
    });
    
    return {
      petitionerAdvocates: petitionerAdvocates.join(', '),
      respondentAdvocates: respondentAdvocates.join(', ')
    };
  };

  const renderKeywordTab = () => (
    <div className="space-y-6">
      {/* Settings List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Settings</h3>
            <button
              onClick={() => setShowNewSettingForm(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add Setting
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading settings...</p>
            </div>
          ) : settings.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No settings found</p>
          ) : (
            <div className="space-y-4">
              {settings.map((setting) => (
                <div key={setting.setting_id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">Keywords: {setting.keywords}</h4>
                      <p className="text-sm text-gray-600">Year Range: {setting.startYear} - {setting.endYear}</p>
                      <p className="text-sm text-gray-600">Locations: {setting.locations?.length || 0} selected</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openEditForm(setting)}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteSetting(setting.setting_id)}
                        className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Scrape Results */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Scrape Results</h3>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="flex space-x-2">
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="Search by keyword..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={scrapeLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {scrapeLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </form>

          {scrapeLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Searching...</p>
            </div>
          ) : scrapeResults.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No results found</p>
          ) : (
            <div className="space-y-4">
              {scrapeResults.map((result) => (
                <div key={result._id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900">Case Number: {result.case_number || '-'}</h4>
                  <p className="text-sm text-gray-600">CNR: {result.cnr_number || '-'}</p>
                  <p className="text-sm text-gray-600">Parties: {result.parties?.join(', ') || '-'}</p>
                  <p className="text-sm text-gray-600">Location: {result.location || '-'}</p>
                  <div className="flex space-x-2 mt-2">
                    <button
                      onClick={() => handleAddToDetail(result.cnr_number)}
                      className="px-3 py-1 text-sm text-green-600 hover:text-green-800"
                    >
                      Add to Detail
                    </button>
                  </div>
                </div>
              ))}
              
              {pagination.hasMore && (
                <div className="text-center pt-4">
                  <button
                    onClick={handleLoadMore}
                    disabled={scrapeLoading}
                    className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    Load More
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderNumberTab = () => (
    <div className="space-y-6">
      {/* Number Settings */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Ecourt CNR Number Settings</h3>
            <button
              onClick={() => setShowNumberSettingForm(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Add CNR Number
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {numberSettingsLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading ecourt number settings...</p>
            </div>
          ) : numberSettings.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No ecourt number settings found</p>
          ) : (
            <div className="space-y-4">
              {numberSettings.map((setting) => (
                <div key={setting._id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-medium text-gray-900">CNR Number: {setting.cnr_number}</h4>
                    </div>
                    <button
                      onClick={() => handleDeleteNumberSetting(setting._id)}
                      className="px-3 py-1 text-sm text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Number Results Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Ecourt Number Results</h3>
        </div>
        
        <div className="p-6">
          {ecourtNumberResultsLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading ecourt number results...</p>
            </div>
          ) : (Array.isArray(ecourtNumberResults) ? ecourtNumberResults : []).length === 0 ? (
            <p className="text-gray-500 text-center py-4">No ecourt number results found</p>
          ) : (
            <div className="space-y-4">
              {(Array.isArray(ecourtNumberResults) ? ecourtNumberResults : []).map((result, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900">CNR Number: {result.cnr_number || '-'}</h4>
                  <p className="text-sm text-gray-600">Status/Stage: {result.caseStatus?.casestage || result.caseStatus?.casestatus || '-'}</p>
                  <p className="text-sm text-gray-600">Petitioner Advocate(s): {Array.isArray(result.petitionerAdvocate) ? result.petitionerAdvocate.join(', ') : result.petitionerAdvocate || '-'}</p>
                  <p className="text-sm text-gray-600">Respondent Advocate(s): {Array.isArray(result.respondentAdvocate) ? result.respondentAdvocate.join(', ') : result.respondentAdvocate || '-'}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return {
    renderKeywordTab,
    renderNumberTab,
    showNewSettingForm,
    setShowNewSettingForm,
    showEditForm,
    setShowEditForm,
    showNumberSettingForm,
    setShowNumberSettingForm,
    newSetting,
    setNewSetting,
    editingSetting,
    setEditingSetting,
    newNumberSetting,
    setNewNumberSetting,
    selectedLocations,
    setSelectedLocations,
    formErrors,
    setFormErrors,
    numberSettingErrors,
    setNumberSettingErrors,
    submitting,
    setSubmitting,
    numberSettingSubmitting,
    setNumberSettingSubmitting,
    locationLoading,
    locationSearchTerm,
    setLocationSearchTerm,
    filteredLocations,
    showLocationDropdown,
    setShowLocationDropdown,
    handleAddSetting,
    handleEditSetting,
    handleAddNumberSetting,
    handleLocationSelect,
    handleLocationRemove,
    handleLocationSearch,
    handleAddToDetail,
    fetchEcourtNumberResults
  };
};

export default EcourtTab; 